import {
  RestoreMethod,
  type RestoreAccount,
  type InventoryResource,
  type Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import type { WriteMode } from './steps/directRestoreWriteModeSelector';
import { DynamoConfiguration } from './steps/dynamoConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreDynamoState {
  resourceId: string;
  snapshot: Snapshot;
  restoredName?: string;
  writeCapacityUnits: number;
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  regionName?: string;
  tags: Record<string, string>;
  initialTags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
  includeLSI: boolean;
  includeGSI: boolean;
  hasLSI: boolean;
  hasGSI: boolean;
  includeAllSecondaryIndexes: boolean;
  restoreMethod: RestoreMethod;
  writeMode: WriteMode;
  skipActualImport: boolean;
}

const restoreDynamoFlow: Step<RestoreDynamoState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'dynamo_configuration',
      Component: DynamoConfiguration,
    },
  ],
};

export const RestoreDynamoWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const hasLSI =
    !!snapshot.resource?.properties?.awsDynamoDb?.localSecondaryIndexes;

  const hasGSI =
    !!snapshot.resource?.properties?.awsDynamoDb?.globalSecondaryIndexes;

  return (
    <Wizard<RestoreDynamoState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreDynamoFlow]}
      initialState={{
        initialRegionName: resource.region || 'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders: [Provider.Aws],
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        includeLSI: hasLSI,
        includeGSI: hasGSI,
        hasLSI,
        hasGSI,
        includeAllSecondaryIndexes: hasLSI && hasGSI,
        restoreMethod: RestoreMethod.RESTORE_METHOD_AUTO,
        writeMode: 'Provisioned',
        writeCapacityUnits: 40_000,
        skipActualImport: false,
      }}
      title={`Restore DynamoDB`}
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
